import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Link,
  Typography,
} from '@mui/material';
import Carousel from '@jobseeker/lotus/components/Carousel';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';

import type {AppState} from '@jobseeker/common-core/types';
import {
  CAT_ID_URL_MAPPING,
  HIRIST_CATEGORY_BLOG_ID_MAPPING,
  HIRIST_CATEGORY_ID_MAPPING,
} from '@jobseeker/common-core/constants';
import {getWindow} from 'ssr-window';
import Image from '@jobseeker/lotus/components/Image';
import {useEffect} from 'react';
import {jobfeedService} from '@jobseeker/common-core/services';

const window = getWindow();

type BlogsPanelProps = {
  heading: string;
};

const DEFAULT_BLOGS = [
  {
    link: 'https://www.hirist.tech/blog/how-to-write-leave-application-for-urgent-work-format-samples/',
    title:
      'How to Write Leave Application for Urgent Work: Format &amp; Samples (2025)',
    imageUrl:
      'https://www.hirist.tech/blog/wp-content/uploads/2025/01/leave-application-for-urgent-work.webp',
  },
  {
    link: 'https://www.hirist.tech/blog/top-90-machine-learning-interview-questions-and-answers/',
    title: 'Top 90+ Machine Learning Interview Questions and Answers',
    imageUrl:
      'https://www.hirist.tech/blog/wp-content/uploads/2025/01/machine-learning-interview-questions.webp',
  },
  {
    link: 'https://www.hirist.tech/blog/top-40-deep-learning-interview-questions-and-answers/',
    title: 'Top 40+ Deep Learning Interview Questions and Answers',
    imageUrl:
      'https://www.hirist.tech/blog/wp-content/uploads/2025/01/deep-learning-interview-questions.webp',
  },
];
const getPageRef = (url: any) => {
  if (url.includes('/c/others')) {
    return 'other-category-page';
  }
  if (url.includes('/c')) {
    return 'category-page';
  }
  if (url.includes('/k')) {
    return 'tag-page';
  }
  if (url.includes('it-jobs-in-')) {
    return 'generic-city-page';
  }
  if (url.includes('jobs-in')) {
    return 'category-city-page';
  }
  if (url.includes('-careers')) {
    return 'company-listing-page';
  }
  if (url.includes('/j/')) {
    return 'jd-page';
  }
  if (url.includes('/jobfeed')) {
    return 'jobfeed-page';
  }
  return '';
};

const BlogsPanel = ({heading}: BlogsPanelProps) => {
  const dispatch = useDispatch();
  const isBotUser = useSelector(
    (state: AppState) => state.app.context.isCrawlerBot,
    shallowEqual
  );
  const categoryId = useSelector(
    (state: AppState) => state.app.context.navigation.selected,
    shallowEqual
  )?.toString();
  let CATEGORY_MAPPED = '';
  const page = useSelector((state: AppState) => state.app.page, shallowEqual);

  const fetchedData = useSelector((state: AppState) => state.ads.blogs);

  let blogPosts = [];
  useEffect(() => {
    if (isBotUser || fetchedData?.length || !categoryId) return;

    const fetchBlogs = async () => {
      try {
        const CATEGORY_MAPPED =
          HIRIST_CATEGORY_BLOG_ID_MAPPING[
            HIRIST_CATEGORY_ID_MAPPING[categoryId?.toString() || ''] ??
              categoryId
          ];
        const blogs = await jobfeedService.getLatestBlogs(
          CATEGORY_MAPPED ?? ''
        );

        const bloData = Object.values(blogs)
          .map((blog: any) => ({
            link: blog.link,
            title: blog.title,
            imageUrl: blog.featuredImage ?? '',
          }))
          .slice(0, 3);

        dispatch({
          type: 'Ads/set_blog_data',
          payload: bloData,
        });
      } catch (e) {
      }
    };

    fetchBlogs();
  }, [isBotUser, fetchedData?.length, categoryId, dispatch]);

  blogPosts = fetchedData?.length > 0 ? fetchedData : DEFAULT_BLOGS;

  const BlogSpots = blogPosts?.map((item, idx) => (
    <Card key={idx} sx={{borderRadius: 2, boxShadow: 3}}>
      <a
        href={item?.link ?? ''}
        target="_blank"
        style={{
          textDecoration: 'none',
        }}
      >
        <CardMedia
          component={"img"}
          height="180"
          image={item.imageUrl}
          alt={'Blog for regisnation'}
        />
        <CardContent sx={{height: 100}}>
          <Typography fontSize={14} fontWeight={600} color={'secondary.dark'}>
            {item.title}
          </Typography>
        </CardContent>
      </a>
    </Card>
  ));

  try {
    const mappedCategoryId =
      HIRIST_CATEGORY_ID_MAPPING[categoryId] ?? categoryId;
    CATEGORY_MAPPED = CAT_ID_URL_MAPPING[mappedCategoryId] ?? '';
  } catch (error) {
    CATEGORY_MAPPED = '';
  }

  const getBlogSideBarViewAllUrl = () => {
    if (
      page == 'category' ||
      page == 'location_based' ||
      page == 'category_location_based' ||
      page == 'tag'
    ) {
      return (
        '/blog/category/technology/' +
        CATEGORY_MAPPED +
        `?ref=${getPageRef(window.location.pathname)}`
      );
    }

    if (page == 'jobdetail') {
      return (
        '/blog/category/interview-questions/' +
        `?ref=${getPageRef(window.location.pathname)}`
      );
    }
    return '/blog/' + `?ref=${getPageRef(window.location.pathname)}`;
  };
  return (
    <>
      {blogPosts?.length > 0 && (
        <>
          <Box
            display={'flex'}
            sx={{
              justifyContent: 'space-between',
              mb: 1.5,
            }}
          >
            <Typography variant="body1">{heading}</Typography>
            <Link
              sx={{
                textDecoration: 'none',
                ':hover': {
                  color: 'secondary.main',
                },
              }}
              href={getBlogSideBarViewAllUrl()}
              target="_blank"
              fontSize={14}
            >
              View All
            </Link>
          </Box>
          <Box sx={{width: '100%', overflow: 'hidden', position: 'relative'}}>
            <Carousel
              carouselItems={BlogSpots}
              slidesPerView={1}
              fullFrame={true}
              showArrows={false}
              swiperProps={{
                spaceBetween: 20,
                slidesPerView: 1,
                pagination: {clickable: true},
                autoplay: {delay: 3000, disableOnInteraction: false},
                style: {
                  width: '100%',
                  display: 'flex',
                },
              }}
              singular={true}
              hideNavigationOnMobile={true}
            />
          </Box>
        </>
      )}
    </>
  );
};

export default BlogsPanel;
